import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { FileText, Shield, ExternalLink, Clock, AlertTriangle, CheckCircle, Star, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIER_LABELS = { 1: 'Post Only', 2: 'Testimonial', 3: 'Paid Ads' };
const TIER_COLORS = { 1: 'text-[#6B7280] bg-white/5', 2: 'text-yellow-400 bg-yellow-400/10', 3: 'text-[#FF4343] bg-[#FF4343]/10' };

function formatNum(n) { if (!n) return '0'; if (n >= 1e6) return `${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `${(n/1e3).toFixed(1)}K`; return n.toLocaleString(); }

function LicenseBadge({ daysRemaining }) {
  if (daysRemaining === undefined || daysRemaining === null) return null;
  const color = daysRemaining >= 60 ? 'text-[#10B981]' : daysRemaining >= 30 ? 'text-yellow-400' : 'text-[#FF4343]';
  return <span className={`text-[9px] font-mono ${color}`}>{daysRemaining}d left</span>;
}

// ==================== ADMIN CONTENT LIBRARY ====================
export function AdminContentLibraryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState('');

  useEffect(() => {
    const params = tierFilter ? `?rights_tier=${tierFilter}` : '';
    axios.get(`${API}/content-library${params}`, { headers: getAuthHeaders() }).then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [tierFilter]);

  return (
    <div data-testid="admin-content-library">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Content Library</h3>
        <div className="flex gap-2">
          {['', 1, 2, 3].map(t => (
            <button key={t} onClick={() => setTierFilter(t)}
              className={`text-[10px] px-3 py-1 border ${tierFilter === t ? 'border-[#FF4343] text-[#FF4343]' : 'border-white/10 text-[#6B7280]'}`}>
              {t === '' ? 'All' : TIER_LABELS[t]}
            </button>
          ))}
        </div>
      </div>
      {loading ? <p className="text-[#9CA3AF]">Loading...</p> : items.length === 0 ? (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <FileText className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#6B7280] text-sm">No licensed content yet. Content appears here when posts are approved in campaigns with Tier 2+ rights.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="content-library-table">
            <thead><tr className="border-b border-white/10">
              {['Executive','Brand','Campaign','Platform','Tier','Impressions','Score','Expires','Ads',''].map(h => (
                <th key={h} className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {items.map(i => (
                <tr key={i.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-2 text-white text-xs">{i.executive_name}</td>
                  <td className="py-2 text-[#9CA3AF] text-xs">{i.brand_name}</td>
                  <td className="py-2 text-[#9CA3AF] text-xs max-w-[120px] truncate">{i.campaign_title}</td>
                  <td className="py-2 text-xs text-[#6B7280] capitalize">{i.post_platform}</td>
                  <td className="py-2"><span className={`text-[9px] font-mono px-1.5 py-0.5 ${TIER_COLORS[i.rights_tier]}`}>{TIER_LABELS[i.rights_tier]}</span></td>
                  <td className="py-2 font-mono text-xs text-[#6B7280]">{formatNum(i.impressions)}</td>
                  <td className="py-2 font-mono text-xs text-[#6B7280]">{i.ai_quality_score || '—'}</td>
                  <td className="py-2 text-xs">
                    {i.license_active ? (
                      <span className={`font-mono text-[10px] ${new Date(i.license_expires_at) > new Date(Date.now() + 60*86400000) ? 'text-[#10B981]' : new Date(i.license_expires_at) > new Date(Date.now() + 30*86400000) ? 'text-yellow-400' : 'text-[#FF4343]'}`}>
                        {new Date(i.license_expires_at).toLocaleDateString()}
                      </span>
                    ) : <span className="text-[10px] text-[#6B7280]">Expired</span>}
                  </td>
                  <td className="py-2">{i.used_in_ads ? <CheckCircle className="w-3.5 h-3.5 text-[#FF4343]" /> : <span className="text-[10px] text-[#6B7280]">—</span>}</td>
                  <td className="py-2">{i.post_url && <a href={i.post_url} target="_blank" rel="noopener noreferrer" className="text-[#6B7280] hover:text-white"><ExternalLink className="w-3 h-3" /></a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ==================== BRAND PORTAL CONTENT LIBRARY ====================
export function BrandContentLibrary({ getHeaders: gh }) {
  const [items, setItems] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adModal, setAdModal] = useState(null);
  const [adForm, setAdForm] = useState({ ad_platform: '', ad_creative_description: '', ad_creative_url: '', ad_placement: '', ad_budget_range: '', ad_run_start: '', ad_run_end: '' });
  const [submittingAd, setSubmittingAd] = useState(false);
  const [renewModal, setRenewModal] = useState(null);

  const headers = gh ? gh() : {};

  useEffect(() => {
    axios.get(`${API}/portal/content-library`, { headers }).then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    axios.post(`${API}/portal/content-library/suggestions`, {}, { headers }).then(r => setSuggestions(r.data?.suggestions)).catch(() => {});
  }, []);

  const submitAdApproval = async () => {
    if (!adModal) return;
    setSubmittingAd(true);
    try {
      await axios.post(`${API}/ad-approvals`, { content_library_id: adModal.id, ...adForm }, { headers });
      setAdModal(null);
      alert('Ad approval request sent to executive');
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setSubmittingAd(false); }
  };

  const handleRenew = async () => {
    if (!renewModal) return;
    try {
      const { data } = await axios.post(`${API}/licenses/${renewModal.id}/renew`, {
        method: 'manual', origin_url: window.location.origin
      }, { headers });
      alert(`License renewed! New expiry: ${new Date(data.new_expiry).toLocaleDateString()}`);
      setRenewModal(null);
      const { data: updated } = await axios.get(`${API}/portal/content-library`, { headers });
      setItems(updated);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const getSuggestion = (contentId) => {
    if (!suggestions) return null;
    for (const [useCase, rec] of Object.entries(suggestions)) {
      if (rec?.content_id === contentId) return useCase.replace(/_/g, ' ');
    }
    return null;
  };

  if (loading) return <p className="text-[#9CA3AF]">Loading content library...</p>;

  return (
    <div data-testid="brand-content-library">
      <h3 className="text-sm font-semibold mb-4">Licensed Content</h3>
      {items.length === 0 ? (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <Shield className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#6B7280] text-sm">No licensed content yet. Content appears here when executives complete campaigns with usage rights.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(i => {
            const suggestion = getSuggestion(i.id);
            const daysColor = (i.days_remaining || 0) >= 60 ? 'text-[#10B981]' : (i.days_remaining || 0) >= 30 ? 'text-yellow-400' : 'text-[#FF4343]';
            return (
              <div key={i.id} className="bg-[#111827] border border-white/10 p-5" data-testid={`content-card-${i.id}`}>
                {suggestion && (
                  <div className="mb-2"><span className="text-[9px] font-mono text-[#FF4343] bg-[#FF4343]/10 px-2 py-0.5">Recommended for {suggestion}</span></div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-sm text-white font-medium">{i.executive_display}</span>
                    <span className="text-[10px] text-[#6B7280] ml-2">{i.executive_title}</span>
                  </div>
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 ${TIER_COLORS[i.rights_tier]}`}>{TIER_LABELS[i.rights_tier]}</span>
                </div>
                {i.post_text && <p className="text-xs text-[#9CA3AF] mb-3 line-clamp-3">{i.post_text}</p>}
                <div className="flex gap-3 text-[10px] text-[#6B7280] mb-3">
                  <span className="capitalize">{i.post_platform}</span>
                  <span className="font-mono">{formatNum(i.impressions)} imp</span>
                  {i.ai_quality_score && <span className="font-mono">Score: {i.ai_quality_score}</span>}
                  <span className={`font-mono ${daysColor}`}>{i.days_remaining}d remaining</span>
                </div>
                {/* Actions */}
                <div className="flex flex-wrap gap-1.5">
                  {i.post_url && (
                    <a href={i.post_url} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] px-2 py-1 border border-white/10 text-[#9CA3AF] hover:text-white transition-colors">
                      View Post <ExternalLink className="w-3 h-3 inline ml-0.5" />
                    </a>
                  )}
                  {i.rights_tier >= 3 && (
                    <button onClick={() => { setAdModal(i); setAdForm({ ad_platform:'', ad_creative_description:'', ad_creative_url:'', ad_placement:'', ad_budget_range:'', ad_run_start:'', ad_run_end:'' }); }}
                      className="text-[10px] px-2 py-1 border border-[#FF4343]/50 text-[#FF4343] hover:bg-[#FF4343] hover:text-white transition-colors" data-testid={`submit-ad-${i.id}`}>
                      Submit Ad for Approval
                    </button>
                  )}
                  {(i.days_remaining || 0) <= 30 && (
                    <button onClick={() => setRenewModal(i)}
                      className="text-[10px] px-2 py-1 border border-yellow-400/50 text-yellow-400 hover:bg-yellow-400 hover:text-[#090E33] transition-colors">
                      Renew License
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ad Approval Modal */}
      {adModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setAdModal(null)} />
          <div className="relative glass max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="ad-approval-modal">
            <h3 className="text-sm font-semibold mb-1">Submit Ad for Approval</h3>
            <p className="text-[10px] text-[#6B7280] mb-4">The executive must approve before this ad can run.</p>
            <div className="space-y-3">
              <select value={adForm.ad_platform} onChange={e => setAdForm({...adForm, ad_platform: e.target.value})}
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                <option value="">Select Ad Platform</option>
                {['LinkedIn', 'Meta', 'X', 'YouTube', 'Display', 'Other'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <textarea placeholder="How you plan to use the content..." value={adForm.ad_creative_description}
                onChange={e => setAdForm({...adForm, ad_creative_description: e.target.value})} rows={3}
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none" />
              <input placeholder="Ad preview URL (optional)" value={adForm.ad_creative_url}
                onChange={e => setAdForm({...adForm, ad_creative_url: e.target.value})}
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
              <div className="grid grid-cols-2 gap-2">
                <input type="date" placeholder="Start" value={adForm.ad_run_start} onChange={e => setAdForm({...adForm, ad_run_start: e.target.value})}
                  className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                <input type="date" placeholder="End" value={adForm.ad_run_end} onChange={e => setAdForm({...adForm, ad_run_end: e.target.value})}
                  className="px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
              </div>
              <select value={adForm.ad_budget_range} onChange={e => setAdForm({...adForm, ad_budget_range: e.target.value})}
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                <option value="">Budget Range (optional)</option>
                {['Under $1K', '$1K–$5K', '$5K–$25K', '$25K+'].map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={submitAdApproval} disabled={submittingAd || !adForm.ad_platform || !adForm.ad_creative_description}
                className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50 transition-all">
                {submittingAd ? 'Submitting...' : 'Submit for Approval'}
              </button>
              <button onClick={() => setAdModal(null)} className="border border-white/20 text-[#9CA3AF] px-4 py-2 text-sm hover:text-white transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Renew Modal */}
      {renewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setRenewModal(null)} />
          <div className="relative glass max-w-sm w-full p-6" data-testid="renew-modal">
            <h3 className="text-sm font-semibold mb-3">Renew License</h3>
            <p className="text-xs text-[#9CA3AF] mb-2">{renewModal.executive_display}'s post</p>
            <p className="text-xs text-[#6B7280] mb-4">Extends license by 90 days from today.</p>
            <div className="flex gap-2">
              <button onClick={handleRenew} className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold hover:bg-[#E03030] transition-all">Renew</button>
              <button onClick={() => setRenewModal(null)} className="border border-white/20 text-[#9CA3AF] px-4 py-2 text-sm hover:text-white transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
